import { Registry } from "@kilat/contracts/tld-pricing";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import { ListPagination, paginate } from "@/components/list-pagination";
import { Item, ItemActions, ItemContent, ItemDescription, ItemTitle } from "@/components/ui/item";
import { AddTldDialog } from "@/features/staff/add-tld/dialog";
import { tldPricingQuery } from "@/features/staff/queries";
import { mayChangePricing } from "@/features/staff/roles";
import {
  arrangeTlds,
  DEFAULT_TLD_SEARCH,
  PRICED_FILTERS,
  STATUS_FILTERS,
  TldPricingTable,
  type TldSearch,
} from "@/features/staff/tld-pricing-table";
import { m } from "@/lib/paraglide/messages.js";

const PAGE_SIZE = 10;

// The search, the filters and the page are route state (guide, rule 16): a link to "Webnic TLDs
// not priced yet" opens just that. Anything the list does not know reads as its default.
const DomainPricingSearch = z.object({
  q: z.string().max(100).optional().catch(undefined),
  registry: z
    .union([Registry, z.literal("all")])
    .optional()
    .catch(undefined),
  status: z.enum(STATUS_FILTERS).optional().catch(undefined),
  priced: z.enum(PRICED_FILTERS).optional().catch(undefined),
  page: z.coerce.number().int().min(1).optional().catch(undefined),
});

// /staff/domains/pricing: the TLDs on sale and what each costs KilatDomain; superadmin and finance
// add, price, order and withdraw them. The buttons are a courtesy, staffGuard is the gate (guide, rule 14).
// The API sends the whole list, which the order needs, so the search, the filters and the page are
// applied here.
export const Route = createFileRoute("/staff/_authenticated/domains/pricing")({
  validateSearch: DomainPricingSearch,
  loader: ({ context }) => context.queryClient.ensureQueryData(tldPricingQuery()),
  staticData: { crumb: m.staff_nav_domain_pricing },
  component: DomainPricingPage,
});

function DomainPricingPage() {
  const { staffMember } = Route.useRouteContext();
  const { page: asked = 1, q, registry, status, priced } = Route.useSearch();
  const navigate = Route.useNavigate();
  // Field by field: a value the schema caught is there as undefined, and must not hide the default.
  const search: TldSearch = {
    q: q ?? DEFAULT_TLD_SEARCH.q,
    registry: registry ?? DEFAULT_TLD_SEARCH.registry,
    status: status ?? DEFAULT_TLD_SEARCH.status,
    priced: priced ?? DEFAULT_TLD_SEARCH.priced,
  };
  const { data: tlds } = useSuspenseQuery(tldPricingQuery());
  const { page, pages, items, range } = paginate(arrangeTlds(tlds, search), asked, PAGE_SIZE);
  const editable = mayChangePricing(staffMember.role);
  // A changed search starts again from the first page; an emptied search box leaves the URL.
  // Replaced, not pushed: Back leaves the list rather than undoing each keystroke.
  const onSearchChange = (next: Partial<TldSearch>) =>
    navigate({
      search: (prev) => ({ ...prev, ...next, q: (next.q ?? prev.q) || undefined, page: undefined }),
      replace: true,
    });
  return (
    <>
      <Item>
        <ItemContent>
          <ItemTitle>{m.staff_nav_domain_pricing()}</ItemTitle>
          <ItemDescription>{m.tld_pricing_description()}</ItemDescription>
        </ItemContent>
        {editable && (
          <ItemActions>
            <AddTldDialog listed={tlds} />
          </ItemActions>
        )}
      </Item>
      <TldPricingTable
        tlds={items}
        all={tlds}
        editable={editable}
        search={search}
        onSearchChange={onSearchChange}
        caption={range}
        pagination={<ListPagination page={page} pages={pages} />}
      />
    </>
  );
}
